<script setup lang="ts">
import {computed, ref, useTemplateRef, watch} from "vue";
import dayjs from "dayjs";
import {TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";
import {weekIndexForDate} from "@/planning/lib/weekPlanning";
import {
    buildGrid,
    buildScheduleSentence,
    DAY_SHORT_LABELS,
    DISPLAY_DAYS,
    formatHours,
    formatSignedHours,
    gridToShifts,
    rangeMinutes,
    ROTATION_PRESETS,
    ScheduleGrid,
    TimeRange,
    toMinutes,
    toTime,
    WEEK_LABELS,
    weekLabel,
    weekMinutes
} from "@/team/planning";
import {teamPlanningApi} from "@/container";
import {ShiftGrid} from "@/core/http/resources/TeamPlanningResource";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";
import BaseButton from "@/core/components/base/BaseButton.vue";
import ScheduleAiFill from "@/team/components/ScheduleAiFill.vue";
import {ArrowLeftRight, CalendarSync, Check, ClipboardCheck, Copy, CopyPlus, MoonStar, Plus, X} from "@lucide/vue";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";

const STEP_MINUTES = 15;
/** Latest bound a range may reach, mirroring toTime()'s own clamp. */
const DAY_LAST_MINUTE = 23 * 60 + 45;
const DEFAULT_DAY: TimeRange[] = [{starts_at: '09:00', ends_at: '19:00'}];
const MAX_WEEKS = WEEK_LABELS.length;

const {schedule, memberId, organizationId} = defineProps<{
    schedule: TeamMemberSchedule | null;
    memberId: number;
    organizationId: number;
}>();

const emits = defineEmits(['schedule-updated']);
const {showSuccess, showError} = useToastStore();

const rotationWeeks = ref<number>(1);
const grid = ref<ScheduleGrid>([]);
const activeWeek = ref<number>(0);
const activeDay = ref<number>(1);
const saving = ref<boolean>(false);

/**
 * Edits not saved yet, AI proposals included. A model so the fiche can hold
 * the pharmacist back before switching member or leaving the screen.
 */
const dirty = defineModel<boolean>('dirty', {default: false});

/**
 * A time field currently holds the caret. Tracked apart from `dirty` because
 * marking the form dirty mid-typing re-renders the input and Vue writes the
 * model back over the character being entered.
 */
const editingBound = ref<boolean>(false);

// ----- AI fill: declared before rebuild(), which the immediate watch below
// runs at setup time. The thread and its requests live in ScheduleAiFill;
// what a proposal does to the grid stays here. -----

const aiFill = useTemplateRef<InstanceType<typeof ScheduleAiFill>>('aiFill');

/** Grid state before the first proposal of the thread: each proposal
 * replaces the whole cycle, so the conversation must be reversible as a
 * whole without losing the edits made before it. */
const beforeFill = ref<{grid: ScheduleGrid; rotationWeeks: number; activeWeek: number; dirty: boolean} | null>(null);

/**
 * Rebuild the local grid from the saved schedule. The grid always holds the
 * maximum rotation: shrinking the cycle only hides the extra weeks, so a
 * preset mis-click cannot destroy them before a save.
 */
const rebuild = () => {
    rotationWeeks.value = schedule?.rotation_weeks ?? 1;
    grid.value = buildGrid(schedule?.shifts ?? [], MAX_WEEKS);
    activeWeek.value = Math.min(activeWeek.value, rotationWeeks.value - 1);
    dirty.value = false;
    // A fresh grid makes the undo snapshot stale: it predates a grid now
    // saved or discarded.
    beforeFill.value = null;
};

watch(() => memberId, () => {
    activeWeek.value = 0;
    activeDay.value = 1;
    rebuild();
}, {immediate: true});

// A revalidation must never clobber edits in progress: the schedule prop
// gets a fresh identity after every sibling save on the fiche.
watch(() => schedule, () => {
    if (!dirty.value && !editingBound.value) {
        rebuild();
    }
});

/** The weeks the current rotation actually uses — the ones shown and saved. */
const visibleWeeks = computed(() => grid.value.slice(0, rotationWeeks.value));

/** Rotation week of today, the only landmark left to tell week A from week B. */
const currentWeek = computed(() => weekIndexForDate(dayjs(), rotationWeeks.value));

const dayRanges = computed(() => grid.value[activeWeek.value]?.[activeDay.value] ?? []);

const sentence = computed(() => buildScheduleSentence(visibleWeeks.value));

/** The grid as the pharmacist sees it, sent to the agent with every turn. */
const currentGrid = computed<ShiftGrid>(() => ({
    rotation_weeks: rotationWeeks.value,
    shifts: gridToShifts(visibleWeeks.value),
}));

/**
 * Contracted minutes per week, read from the schedule prop rather than the
 * local grid so an edit of the profile card shows up at once, even mid-edit.
 */
const contractMinutes = computed(() =>
    schedule?.weekly_hours ? Math.round(schedule.weekly_hours * 60) : null
);

/**
 * Planned minus contracted minutes, one entry per visible week. Informative
 * only: an uneven cycle or a temporary overtime is the pharmacist's call.
 */
const weekGaps = computed(() => {
    const contract = contractMinutes.value;

    return visibleWeeks.value.map(week => contract === null ? null : weekMinutes(week) - contract);
});

const gapTitle = (gap: number): string => {
    const contract = formatHours(contractMinutes.value ?? 0);

    if (gap === 0) {
        return `Conforme au contrat (${contract} par semaine)`;
    }

    return `${formatHours(Math.abs(gap))} de ${gap > 0 ? 'plus' : 'moins'} que le contrat (${contract} par semaine)`;
};

const touch = () => dirty.value = true;

const setRotation = (weeks: number) => {
    rotationWeeks.value = weeks;
    activeWeek.value = Math.min(activeWeek.value, weeks - 1);
    touch();
};

const toggleDay = () => {
    const day = grid.value[activeWeek.value][activeDay.value];

    grid.value[activeWeek.value][activeDay.value] = day.length
        ? []
        : DEFAULT_DAY.map(range => ({...range}));
    touch();
};

/**
 * Window a bound may move in: at least one step of duration, and no overlap
 * with the neighbour ranges. Shared by the steppers and the manual entry.
 */
const boundLimits = (ranges: TimeRange[], rangeIndex: number, bound: 'starts_at' | 'ends_at') => {
    const range = ranges[rangeIndex];

    const min = bound === 'starts_at'
        ? (rangeIndex > 0 ? toMinutes(ranges[rangeIndex - 1].ends_at) : 0)
        : toMinutes(range.starts_at) + STEP_MINUTES;
    const max = bound === 'starts_at'
        ? toMinutes(range.ends_at) - STEP_MINUTES
        : (rangeIndex < ranges.length - 1 ? toMinutes(ranges[rangeIndex + 1].starts_at) : DAY_LAST_MINUTE);

    // Never let the window invert: clamping between a min above its max would
    // silently move the bound the wrong way instead of holding it still.
    return {min, max: Math.max(min, max)};
};

/** Move a range bound by one step. */
const nudge = (rangeIndex: number, bound: 'starts_at' | 'ends_at', direction: 1 | -1) => {
    const ranges = grid.value[activeWeek.value][activeDay.value];
    const range = ranges[rangeIndex];
    const next = toMinutes(range[bound]) + direction * STEP_MINUTES;
    const {min, max} = boundLimits(ranges, rangeIndex, bound);

    range[bound] = toTime(Math.min(Math.max(next, min), max));
    touch();
};

/**
 * Manual entry of a bound: snapped to the steppers' 15-minute grid, then
 * clamped into the same window as them. Committed on blur only — Chromium
 * fires `change` on every keystroke of a time input, and snapping mid-typing
 * rewrites the field under the user's fingers. The input is realigned by
 * hand: when snap and clamp land on the value the model already holds, no
 * re-render would otherwise erase the typed text.
 *
 * Nothing reactive may change while these fields are being typed into: Vue
 * force-patches the `value` prop on every re-render, so a render triggered
 * mid-entry writes the model back over the half-typed character. That is why
 * there is no `@input` handler here, and why the save button stays enabled
 * while a field holds the caret: the blur that commits the entry fires
 * before the click, and a button still disabled at that moment would swallow
 * the click.
 */
const enterBound = (rangeIndex: number, bound: 'starts_at' | 'ends_at', event: Event) => {
    const input = event.target as HTMLInputElement;
    const ranges = grid.value[activeWeek.value][activeDay.value];
    const range = ranges[rangeIndex];

    editingBound.value = false;

    if (/^\d{2}:\d{2}$/.test(input.value)) {
        const snapped = Math.round(toMinutes(input.value) / STEP_MINUTES) * STEP_MINUTES;
        const {min, max} = boundLimits(ranges, rangeIndex, bound);
        const next = toTime(Math.min(Math.max(snapped, min), max));

        // Only a real change marks the form dirty: focusing a field and
        // tabbing away must not enable a save that would rewrite the whole
        // grid over whatever another workstation stored meanwhile.
        if (next !== range[bound]) {
            range[bound] = next;
            touch();
        }
    }

    input.value = range[bound];
};

/**
 * Split the day: append a range after the last one.
 */
const breakStart = (ranges: TimeRange[]): number => {
    const lastEnd = ranges.length ? toMinutes(ranges[ranges.length - 1].ends_at) : toMinutes('09:00');

    // An hour of break when the day allows it, but always after the previous
    // range: capping the start without that floor produced overlapping ranges
    // on days ending late, which then broke every bound computation.
    return Math.max(Math.min(lastEnd + 60, 22 * 60), lastEnd + STEP_MINUTES);
};

const canAddBreak = computed(() =>
    breakStart(dayRanges.value) + STEP_MINUTES <= DAY_LAST_MINUTE
);

const addBreak = () => {
    const ranges = grid.value[activeWeek.value][activeDay.value];
    const start = breakStart(ranges);

    if (start + STEP_MINUTES > DAY_LAST_MINUTE) {
        return;
    }

    ranges.push({starts_at: toTime(start), ends_at: toTime(Math.min(start + 120, DAY_LAST_MINUTE))});
    touch();
};

const removeRange = (rangeIndex: number) => {
    grid.value[activeWeek.value][activeDay.value].splice(rangeIndex, 1);
    touch();
};

const copyDay = (day: TimeRange[]): TimeRange[] => day.map(range => ({...range}));

/**
 * Copy the current day's ranges to every other worked day, of the week shown
 * or of every week of the cycle: a new lunch break is one action, not one
 * per day and per week.
 */
const applyToWorkedDays = (wholeCycle: boolean) => {
    const source = grid.value[activeWeek.value][activeDay.value];

    grid.value = grid.value.map((week, weekIndex) => {
        const inScope = wholeCycle ? weekIndex < rotationWeeks.value : weekIndex === activeWeek.value;

        return !inScope ? week : week.map((day, dayIndex) =>
            day.length && (weekIndex !== activeWeek.value || dayIndex !== activeDay.value) ? copyDay(source) : day
        );
    });
    touch();
};

// ----- Moves between the weeks of the cycle -----

/** A schedule entered on the wrong week of a two-week cycle is one click away from right. */
const swapWeeks = () => {
    grid.value = [grid.value[1], grid.value[0], ...grid.value.slice(2)];
    touch();
};

/** The weeks of the cycle the shown week would be copied over. */
const copyTargets = computed(() =>
    visibleWeeks.value.map((_, index) => index).filter(index => index !== activeWeek.value)
);

const activeWeekEmpty = computed(() => visibleWeeks.value[activeWeek.value]?.every(day => !day.length) ?? true);

/** Targets holding hours the copy would wipe, asked about first. */
const overwrittenWeeks = ref<number[]>([]);

const copyWeek = () => {
    const source = grid.value[activeWeek.value];

    grid.value = grid.value.map((week, index) =>
        copyTargets.value.includes(index) ? source.map(copyDay) : week
    );
    overwrittenWeeks.value = [];
    touch();
};

const requestCopyWeek = () => {
    const overwritten = copyTargets.value.filter(index => grid.value[index].some(day => day.length));

    if (overwritten.length) {
        overwrittenWeeks.value = overwritten;

        return;
    }

    copyWeek();
};

/** A week's name inside a sentence: « la semaine A », « la semaine B ». */
const weekInSentence = (index: number): string => {
    const label = weekLabel(index);

    return `la ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
};

const overwriteQuestion = computed(() => {
    const weeks = overwrittenWeeks.value;
    const source = weekInSentence(activeWeek.value);

    if (weeks.length === 1) {
        return `${weekInSentence(weeks[0])} contient déjà des horaires. La remplacer par ceux de ${source} ?`
            .replace(/^l/, 'L');
    }

    // Several targets only exist on cycles of three or four, named by letter.
    const letters = weeks.map(index => WEEK_LABELS[index].replace('Semaine ', ''));

    return `Les semaines ${letters.slice(0, -1).join(', ')} et ${letters.at(-1)} contiennent déjà des horaires. `
        + `Les remplacer par ceux de ${source} ?`;
});

// ----- AI natural-language fill -----

/**
 * Load a proposal into the grid without saving it: the pharmacist reviews it
 * and presses the regular save button. The grid is snapshotted on the first
 * proposal only — the undo button goes back to before the conversation, the
 * thread itself is where a pharmacist goes back one proposal.
 */
const applySuggestion = (proposal: ShiftGrid) => {
    beforeFill.value ??= {
        grid: grid.value.map(week => week.map(day => day.map(range => ({...range})))),
        rotationWeeks: rotationWeeks.value,
        activeWeek: activeWeek.value,
        dirty: dirty.value,
    };
    grid.value = buildGrid(proposal.shifts, MAX_WEEKS);
    rotationWeeks.value = proposal.rotation_weeks;
    activeWeek.value = 0;
    touch();
};

const undoFill = () => {
    const previous = beforeFill.value;

    if (!previous) {
        return;
    }

    grid.value = previous.grid;
    rotationWeeks.value = previous.rotationWeeks;
    activeWeek.value = previous.activeWeek;
    dirty.value = previous.dirty;
    beforeFill.value = null;
};

const dayTotal = computed(() =>
    formatHours(dayRanges.value.reduce((sum, range) => sum + rangeMinutes(range), 0))
);

/**
 * Save the visible weeks. Resolves whether the grid is saved, so the fiche
 * can save on the pharmacist's behalf before switching member or screen and
 * stay put when it fails.
 */
const save = async (): Promise<boolean> => {
    if (!dirty.value) {
        return true;
    }

    if (saving.value) {
        return false;
    }

    saving.value = true;

    try {
        const updated = await teamPlanningApi().syncShifts(organizationId, memberId, {
            rotation_weeks: rotationWeeks.value,
            shifts: gridToShifts(visibleWeeks.value)
        });

        emits('schedule-updated', updated);
        dirty.value = false;
        // A saved proposal closes the conversation: picking it up again
        // starts from the saved grid, which every turn sends anyway.
        aiFill.value?.reset();
        showSuccess({content: 'Planning enregistré'});

        return true;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            showError({content: apiErrorMessage(error, 'Le planning n\'a pas pu être enregistré')});
        }

        return false;
    } finally {
        saving.value = false;
    }
};

defineExpose({save});
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div class="flex items-center gap-2">
      <CalendarSync class="size-4.5 text-primary" />
      <div class="flex-1 text-sm font-semibold text-gray-800">
        Planning récurrent
      </div>
      <span
        v-if="dirty"
        class="flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800"
      >
        <span class="size-1.5 rounded-full bg-amber-500" />
        Modifications non enregistrées
      </span>
      <span v-else class="flex items-center gap-1 text-[11px] text-gray-400">
        <Check v-if="schedule?.shifts.length" class="size-3.5" />
        {{ schedule?.shifts.length ? 'Planning enregistré' : 'Aucun planning enregistré' }}
      </span>
      <BaseButton
        primary
        :loading="saving"
        :disabled="!dirty && !editingBound"
        @click="save"
      >
        Enregistrer le planning
      </BaseButton>
    </div>
    <p class="mt-1 text-xs text-gray-400">
      Décrivez les horaires à l'IA, elle vous pose ses questions si besoin et remplit le planning ; ou choisissez une alternance type.
    </p>

    <ScheduleAiFill
      ref="aiFill"
      class="mt-3"
      :member-id="memberId"
      :organization-id="organizationId"
      :grid="currentGrid"
      :can-undo="beforeFill !== null"
      @proposal="applySuggestion"
      @undo="undoFill"
    />

    <div class="mt-4 flex flex-wrap gap-2">
      <button
        v-for="preset in ROTATION_PRESETS"
        :key="preset.weeks"
        class="h-8 rounded-lg border px-3 text-xs font-semibold transition-colors"
        :class="rotationWeeks === preset.weeks
          ? 'border-primary bg-primary text-white'
          : 'border-gray-200 bg-white text-gray-600 hover:border-primary/40'"
        @click="setRotation(preset.weeks)"
      >
        {{ preset.label }}
      </button>
      <span v-if="contractMinutes !== null" class="ml-auto self-center text-xs text-gray-400">
        Contrat : <span class="font-semibold text-gray-600">{{ formatHours(contractMinutes) }}</span> / semaine
      </span>
    </div>

    <!-- Rotation preview grid -->
    <div class="mt-4 grid gap-2.5" :style="{gridTemplateColumns: `repeat(${rotationWeeks}, minmax(0, 1fr))`}">
      <div
        v-for="(week, weekIndex) in visibleWeeks"
        :key="weekIndex"
        class="rounded-lg border p-2 transition-colors"
        :class="weekIndex === activeWeek ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50 cursor-pointer'"
        @click="activeWeek = weekIndex"
      >
        <div class="mb-1.5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[10px] font-bold text-gray-500">
          <span>
            {{ rotationWeeks > 1 ? weekLabel(weekIndex) : 'Chaque semaine' }}
            <span v-if="rotationWeeks > 1 && weekIndex === currentWeek" class="font-normal text-primary"> · cette semaine</span>
            <span class="font-normal text-gray-400"> · {{ formatHours(weekMinutes(week)) }}</span>
          </span>
          <span
            v-if="weekGaps[weekIndex] !== null"
            class="flex items-center gap-0.5 rounded px-1 py-px font-semibold"
            :class="weekGaps[weekIndex] === 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'"
            :title="gapTitle(weekGaps[weekIndex]!)"
          >
            <template v-if="weekGaps[weekIndex] === 0">
              <Check class="size-3" />
              Contrat
            </template>
            <template v-else>
              {{ formatSignedHours(weekGaps[weekIndex]!) }}
            </template>
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <button
            v-for="day in DISPLAY_DAYS"
            :key="day"
            class="flex items-center justify-between rounded px-1.5 py-0.5 text-[10px] transition-colors"
            :class="[
              weekIndex === activeWeek && day === activeDay ? 'bg-primary text-white' :
              week[day].length ? 'bg-white text-gray-700' : 'text-gray-300'
            ]"
            @click.stop="activeWeek = weekIndex; activeDay = day"
          >
            <span class="font-semibold">{{ DAY_SHORT_LABELS[day] }}</span>
            <span>{{ week[day].length ? week[day].map(r => `${r.starts_at}–${r.ends_at}`).join(' · ') : '—' }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="rotationWeeks > 1" class="mt-2.5 flex flex-wrap justify-end gap-2">
      <button
        v-if="rotationWeeks === 2"
        class="flex h-7 items-center gap-1.5 rounded-lg border border-solid border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-primary hover:border-primary/40"
        @click="swapWeeks"
      >
        <ArrowLeftRight class="size-3.5" />
        Inverser les semaines
      </button>
      <button
        class="flex h-7 items-center gap-1.5 rounded-lg border border-solid border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-primary hover:border-primary/40 disabled:opacity-50 disabled:hover:border-gray-200"
        :disabled="activeWeekEmpty"
        :title="activeWeekEmpty ? 'Cette semaine ne contient aucun horaire à copier' : undefined"
        @click="requestCopyWeek"
      >
        <CopyPlus class="size-3.5" />
        Copier {{ weekInSentence(activeWeek) }} vers {{ rotationWeeks === 2 ? 'l\'autre semaine' : 'les autres semaines' }}
      </button>
    </div>

    <AlertDialog
      :open="overwrittenWeeks.length > 0"
      @update:open="open => { if (!open) overwrittenWeeks = [] }"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remplacer les horaires ?</AlertDialogTitle>
          <AlertDialogDescription>
            {{ overwriteQuestion }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <BaseButton primary @click="copyWeek">
            Remplacer
          </BaseButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- Day editor -->
    <div class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3.5">
      <div class="flex flex-wrap items-center gap-2">
        <div class="flex-1 text-xs font-bold text-gray-700">
          {{ rotationWeeks > 1 ? `${weekLabel(activeWeek)} — ` : '' }}{{ DAY_SHORT_LABELS[activeDay] }}
        </div>
        <span
          v-if="dayRanges.length"
          class="rounded-md bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-700"
        >
          {{ dayTotal }}
        </span>
        <button
          class="h-7 rounded-lg border px-2.5 text-[11px] font-semibold transition-colors"
          :class="dayRanges.length
            ? 'border-gray-200 bg-white text-gray-500 hover:text-red-500'
            : 'border-primary bg-primary text-white'"
          @click="toggleDay"
        >
          {{ dayRanges.length ? 'Jour non travaillé' : 'Activer ce jour' }}
        </button>
      </div>

      <div v-if="dayRanges.length" class="mt-3 flex flex-col gap-2">
        <div
          v-for="(range, rangeIndex) in dayRanges"
          :key="rangeIndex"
          class="flex flex-wrap items-center gap-2.5"
        >
          <span class="w-16 text-[11px] font-semibold text-gray-400">
            {{ rangeIndex === 0 ? 'Matin / 1' : `Plage ${rangeIndex + 1}` }}
          </span>
          <div v-for="bound in (['starts_at', 'ends_at'] as const)" :key="bound" class="flex items-center gap-1">
            <button
              class="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-primary/40"
              @click="nudge(rangeIndex, bound, -1)"
            >
              −
            </button>
            <input
              type="time"
              :value="range[bound]"
              :title="bound === 'starts_at' ? 'Heure de début' : 'Heure de fin'"
              class="recurrence-time h-7 w-16 rounded-md border border-gray-200 bg-white text-center text-xs font-bold text-gray-700 focus:border-primary/60 focus:outline-none"
              @focus="editingBound = true"
              @blur="enterBound(rangeIndex, bound, $event)"
              @keydown.enter="($event.target as HTMLInputElement).blur()"
            >
            <button
              class="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-primary/40"
              @click="nudge(rangeIndex, bound, 1)"
            >
              +
            </button>
          </div>
          <button
            v-if="dayRanges.length > 1"
            title="Supprimer la plage"
            class="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-red-400 hover:text-red-600"
            @click="removeRange(rangeIndex)"
          >
            <X class="size-3.5" />
          </button>
        </div>

        <div class="mt-1 flex flex-wrap gap-2">
          <button
            class="flex h-7 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-primary hover:border-primary/40 disabled:opacity-50 disabled:hover:border-gray-200"
            :disabled="!canAddBreak"
            :title="canAddBreak ? undefined : 'La journée est déjà remplie jusqu\'à 23h45'"
            @click="addBreak"
          >
            <Plus class="size-3.5" />
            Ajouter une coupure
          </button>
          <button
            class="flex h-7 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-primary hover:border-primary/40"
            @click="applyToWorkedDays(false)"
          >
            <Copy class="size-3.5" />
            {{ rotationWeeks > 1 ? 'Appliquer aux jours travaillés de cette semaine' : 'Appliquer à tous les jours travaillés' }}
          </button>
          <button
            v-if="rotationWeeks > 1"
            class="flex h-7 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-primary hover:border-primary/40"
            @click="applyToWorkedDays(true)"
          >
            <Copy class="size-3.5" />
            Appliquer aux jours travaillés de toutes les semaines
          </button>
        </div>
      </div>
      <div
        v-else
        class="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-400"
      >
        <MoonStar class="size-4" />
        Jour non travaillé — activez-le pour définir des horaires.
      </div>
    </div>

    <!-- Summary sentence -->
    <div class="mt-4 flex items-start gap-2.5 rounded-lg bg-primary/5 px-3.5 py-3">
      <ClipboardCheck class="size-4 shrink-0 text-primary" />
      <p class="text-xs font-medium leading-relaxed text-primary/90">
        {{ sentence }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* The native picker icon would double the field's width for nothing: the
   steppers already cover the no-keyboard path. */
.recurrence-time::-webkit-calendar-picker-indicator {
    display: none;
}
</style>
